/**
 * Helpers for Involvement drill-down (sessions from Data Capture export only).
 * Matches deliverer / org logic used in scripts/seed_final_tables.py aggregates.
 */

import { sumSessionParticipants } from '../dataService';

/** Collapse whitespace for DelivererName matching (e.g. "Johan  Muller" vs "Johan Muller"). */
export function normalizeWhitespace(str) {
  if (str == null || str === '') return '';
  return String(str).trim().replace(/\s+/g, ' ');
}

/**
 * @param {object[]} sessions — kwata_final_sessions rows
 * @param {string} delivererName — development officer name from aggregated row
 * @returns {object[]}
 */
export function filterSessionsByDeliverer(sessions, delivererName) {
  const target = normalizeWhitespace(delivererName);
  if (!target) return [];
  return (sessions || []).filter((s) => normalizeWhitespace(s.deliverer_name) === target);
}

/**
 * @param {object[]} sessions
 * @param {{ name: string, region?: string|null }} org — row from top_organisations
 */
export function filterSessionsByOrganisation(sessions, org) {
  const name = normalizeWhitespace(org?.name);
  if (!name) return [];
  return (sessions || []).filter((s) => {
    if (normalizeWhitespace(s.organisation) !== name) return false;
    if (org.region != null && org.region !== '' && s.region && s.region !== org.region) return false;
    return true;
  });
}

/**
 * Parse StartDate from a session row (Excel serial, ISO-like string, or Date-parsable).
 * @returns {Date|null}
 */
export function parseSessionStartDate(s) {
  const d = s?.start_date;
  if (d == null || d === '') return null;
  if (typeof d === 'number' && d > 30000) {
    const epoch = new Date(Date.UTC(1899, 11, 30) + d * 86400000);
    if (!Number.isNaN(epoch.getTime())) return epoch;
  }
  const str = String(d);
  const iso = str.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return new Date(Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])));
  }
  const dt = new Date(str);
  if (!Number.isNaN(dt.getTime())) return dt;
  return null;
}

/** @returns {string|null} YYYY-MM-DD in UTC */
export function sessionDayKey(s) {
  const dt = parseSessionStartDate(s);
  if (!dt) return null;
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}

/**
 * Build sorted series for charts: one point per calendar day with activity.
 * @param {object[]} sessions — filtered list
 */
export function buildDailySeries(sessions) {
  const byDay = {};
  (sessions || []).forEach((s) => {
    const day = sessionDayKey(s);
    if (!day) return;
    if (!byDay[day]) {
      byDay[day] = { day, reports: 0, participants: 0, deliveryInstances: 0 };
    }
    byDay[day].reports += 1;
    byDay[day].participants += sumSessionParticipants(s);
    byDay[day].deliveryInstances += Number(s.num_sessions) || 0;
  });
  return Object.values(byDay).sort((a, b) => a.day.localeCompare(b.day));
}

/** Monday (UTC) YYYY-MM-DD for a calendar day string */
function weekStartMonday(dayStr) {
  const [y, m, d] = dayStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const dow = dt.getUTCDay();
  const delta = dow === 0 ? -6 : 1 - dow;
  dt.setUTCDate(dt.getUTCDate() + delta);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}

/**
 * If many days, bucket by calendar week (Monday start) for readability.
 * @param {{ day: string, reports: number, participants: number, deliveryInstances: number }[]} daily
 */
export function maybeBucketByWeek(daily) {
  if (!daily.length || daily.length <= 45) return { series: daily, bucket: 'day' };
  const byWeek = {};
  daily.forEach((row) => {
    const label = weekStartMonday(row.day);
    if (!byWeek[label]) {
      byWeek[label] = { day: label, reports: 0, participants: 0, deliveryInstances: 0 };
    }
    byWeek[label].reports += row.reports;
    byWeek[label].participants += row.participants;
    byWeek[label].deliveryInstances += row.deliveryInstances;
  });
  const series = Object.values(byWeek).sort((a, b) => a.day.localeCompare(b.day));
  return { series, bucket: 'week' };
}

export function countByField(rows, field) {
  const counts = {};
  (rows || []).forEach((r) => {
    const v = r[field];
    const k = v != null && String(v).trim() !== '' ? String(v).trim() : '—';
    counts[k] = (counts[k] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

/**
 * Summary stats for a filtered session list.
 */
export function sessionListStats(sessions) {
  const list = sessions || [];
  const dayKeys = list.map(sessionDayKey).filter(Boolean).sort();
  const daily = buildDailySeries(list);
  const distinctDays = daily.length;
  let maxReportsOneDay = 0;
  daily.forEach((d) => {
    if (d.reports > maxReportsOneDay) maxReportsOneDay = d.reports;
  });
  const totalDelivery = list.reduce((s, r) => s + (Number(r.num_sessions) || 0), 0);
  const rowsWithDate = list.filter((s) => sessionDayKey(s)).length;
  const first = dayKeys[0] || null;
  const last = dayKeys[dayKeys.length - 1] || null;
  return {
    firstDay: first,
    lastDay: last,
    distinctDaysWithReports: distinctDays,
    rowsWithDate,
    rowsMissingDate: list.length - rowsWithDate,
    maxReportsOneDay,
    totalDeliveryInstances: totalDelivery,
  };
}
